import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  where,
  type CollectionReference,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { wordleGameDocumentConverter } from "@kekkle/shared/convertors/web/wordle-game-document.converter";
import { wordle6GameStateDocumentConverter } from "@kekkle/shared/convertors/web/wordle6-game-state-document.converter";
import type { WordleGameDocument } from "@kekkle/shared/types/documents/wordle-game-document/wordle-game-document";
import type { Wordle6GameStateDocument } from "@kekkle/shared/types/documents/wordle6-game-state-document/wordle6-game-state-document";

function wordle6GamesCollection(): CollectionReference<WordleGameDocument> {
  return collection(firestore, "wordle6_games").withConverter(
    wordleGameDocumentConverter,
  );
}

function gameStatesCollection(
  matchId: string,
): CollectionReference<Wordle6GameStateDocument> {
  return collection(
    firestore,
    "wordle6_games",
    matchId,
    "game_states",
  ).withConverter(wordle6GameStateDocumentConverter);
}

export async function saveGameState(
  matchId: string,
  userUuid: string,
  payload: Omit<Wordle6GameStateDocument, "_id">,
): Promise<void> {
  if (!matchId || !userUuid) {
    throw new Error("MatchId or userUuid not set");
  }
  await setDoc(doc(gameStatesCollection(matchId), userUuid), payload);
}

export async function fetchGameState(
  matchId: string,
  userUuid: string,
): Promise<string[] | null> {
  const stateDoc = await getDoc(doc(gameStatesCollection(matchId), userUuid));
  if (stateDoc.exists()) {
    const data = stateDoc.data();
    return data.guesses;
  }
  return null;
}

export async function fetchNewestGame(
  language: string = "NL",
): Promise<WordleGameDocument | null> {
  const matchQuery = query(
    wordle6GamesCollection(),
    orderBy("date", "desc"),
    where("language", "==", language),
    limit(1),
  );
  const querySnapshot = await getDocs(matchQuery);
  if (querySnapshot.empty) {
    return null;
  }
  return querySnapshot.docs[0]?.data() ?? null;
}

export async function fetchGamePerDate(
  date: Date,
  language: string = "NL",
): Promise<WordleGameDocument | null> {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  const matchQuery = query(
    wordle6GamesCollection(),
    where("date", ">=", startOfDay),
    where("date", "<=", endOfDay),
    where("language", "==", language),
    orderBy("date", "desc"),
    limit(1),
  );
  const querySnapshot = await getDocs(matchQuery);
  if (querySnapshot.empty) {
    return null;
  }
  return querySnapshot.docs[0]?.data() ?? null;
}

export async function fetchGameIdPerDate(
  date: Date,
  language: string = "NL",
): Promise<string | null> {
  const gameDoc = await fetchGamePerDate(date, language);
  return gameDoc?._id ?? null;
}
